/**
 * Modulo **piano dei test** — layer API (BE `BE-CONTRACT-FOR-FE-QA-TEST-PLAN-v1`, BE v1.79.0).
 *
 * Quattro risorse JSON:API più due endpoint dedicati:
 *  · `test-plans`               — la campagna (ambiente + versione dichiarati)
 *  · `test-cases`               — il catalogo dei flussi, riusabile fra campagne
 *  · `test-case-runs`           — le esecuzioni: **l'esito sta qui, non sul flusso**
 *  · `test-case-run-attachments`— le prove (screenshot/log/…)
 *  · `POST test-case-runs/{id}/report-issue` — crea il Task di segnalazione già compilato
 *  · `GET  test-plans/{id}/coverage`         — copertura aggregata sull'ULTIMA run per flusso
 *
 * Regole del contratto che qui sono codificate una volta sola:
 *  · `severity` obbligatoria se `outcome=fail`, `blockedReason` se `outcome=blocked`
 *    (validate anche lato client: `validateRunDraft`, così l'utente non aspetta il 422);
 *  · `appVersion`/`environment` sull'esecuzione si OMETTONO per ereditarli dalla campagna;
 *  · `executedAt`, `executedByEmployee`, `isSeeded`, `issueTask` sono **server-side**: non
 *    vanno mai nel payload (l'ultimo lo scrive solo `/report-issue`);
 *  · `startDate`/`endDate` della campagna sono **date pure** (`YYYY-MM-DD`).
 *
 * Nota sul paging: il catalogo cresce (43 flussi ora, ~293 annunciati) e l'adapter clampa
 * `page.size` a 100 → per il catalogo e per le run di una campagna si usa `fetchAllPages`,
 * altrimenti si troncherebbe in silenzio.
 */
import { createOne, deleteOne, fetchAllPages, fetchList, updateOne } from '@/shared/api/jsonapi/client';
import { toDateOnly } from '@/shared/lib/format';
import { httpRequest } from '@/shared/api/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import type { FilterClause } from '@/shared/api/jsonapi/types';
import type { DecodedJwt } from '@/features/auth/types';

// ─────────────────────────────── Vocabolari ───────────────────────────────

/**
 * I moduli ammessi su `test-case.module`: copia fedele di `QaVocab.Modules` sul BE
 * (`Src/MIT.Fwk.WebApi/Services/QaResourceDefinitions.cs`), che li **valida in scrittura** sia
 * in create sia in update — un valore fuori lista prende `422 TEST_INVALID_MODULE`. Non esiste
 * un endpoint che esponga il vocabolario: questa lista va tenuta allineata a mano, e i giri sono
 * commentati come là per rendere evidente cosa è arrivato dopo.
 *
 * ⚠️ Serve a popolare i `Select` di scrittura, **non** a filtrare quello che si legge: il
 * perimetro di una campagna è la stringa che ha scritto il BE (vedi `parseModules`).
 */
export const TEST_MODULES = [
  // primo giro
  'leave', 'timesheet',
  // secondo giro — i moduli verticali
  'smart-working', 'fleet', 'travel', 'clock-in', 'shift', 'meeting-room',
  'service-report', 'projects', 'vps', 'planner', 'sprint',
  // terzo giro — le aree trasversali
  'platform', 'roles', 'users', 'holding', 'tenant-config', 'changelog',
  'external-sync', 'qa',
  // quarto giro — i moduli nati dopo il 7 agosto
  'support', 'client-portal', 'billing', 'task', 'attendance', 'orders',
  'expense', 'security',
  // storici, tenuti per retrocompatibilità con i casi scritti a mano
  'hr', 'admin', 'auth', 'other',
] as const;
export type TestModule = typeof TEST_MODULES[number];

/** Etichette dei moduli. Chi legge fa sempre fallback sullo slug: uno slug nuovo non rompe nulla. */
export const MODULE_LABEL: Record<string, string> = {
  leave: 'Assenze', timesheet: 'Rendicontazione', 'smart-working': 'Smart working',
  fleet: 'Flotta', travel: 'Trasferte', 'clock-in': 'Timbrature', shift: 'Turni',
  'meeting-room': 'Sale riunioni', 'service-report': 'Rapportini', projects: 'Progetti',
  vps: 'VPS', planner: 'Pianificazione', sprint: 'Sprint', platform: 'Piattaforma',
  roles: 'Ruoli', users: 'Utenti', holding: 'Gruppo societario',
  'tenant-config': 'Configurazione azienda', changelog: 'Novità',
  'external-sync': 'Sincronizzazioni', qa: 'Collaudo', support: 'Assistenza',
  'client-portal': 'Portale clienti', billing: 'Fatturazione', task: 'Attività',
  attendance: 'Presenze', orders: 'Ordini', expense: 'Note spese', security: 'Sicurezza',
  hr: 'HR', admin: 'Amministrazione', auth: 'Accessi', other: 'Altro',
};

export const TEST_PRIORITIES = ['critical', 'high', 'medium', 'low'] as const;
export type TestPriority = typeof TEST_PRIORITIES[number];
export const PRIORITY_LABEL: Record<string, string> = {
  critical: 'Critica', high: 'Alta', medium: 'Media', low: 'Bassa',
};

export const TEST_OUTCOMES = ['not-run', 'pass', 'fail', 'blocked', 'skipped'] as const;
export type TestOutcome = typeof TEST_OUTCOMES[number];
export const OUTCOME_LABEL: Record<string, string> = {
  'not-run': 'Da eseguire', pass: 'Passato', fail: 'Fallito',
  blocked: 'Bloccato', skipped: 'Saltato',
};

export const TEST_SEVERITIES = ['blocker', 'major', 'minor', 'cosmetic'] as const;
export type TestSeverity = typeof TEST_SEVERITIES[number];
export const SEVERITY_LABEL: Record<string, string> = {
  blocker: 'Bloccante', major: 'Grave', minor: 'Minore', cosmetic: 'Estetico',
};

export const TEST_ENVIRONMENTS = ['dev', 'staging', 'production'] as const;
export type TestEnvironment = typeof TEST_ENVIRONMENTS[number];

export const PLAN_STATUSES = ['draft', 'active', 'completed', 'archived'] as const;
export type PlanStatus = typeof PLAN_STATUSES[number];
export const PLAN_STATUS_LABEL: Record<string, string> = {
  draft: 'Bozza', active: 'Attiva', completed: 'Chiusa', archived: 'Archiviata',
};

export const ATTACHMENT_TYPES = ['screenshot', 'video', 'log', 'har', 'other'] as const;
export type AttachmentType = typeof ATTACHMENT_TYPES[number];
export const ATTACHMENT_TYPE_LABEL: Record<string, string> = {
  screenshot: 'Screenshot', video: 'Video', log: 'Log', har: 'HAR', other: 'Altro',
};

/** Colori Mantine per l'esito (usati da badge e barre di copertura). */
export const OUTCOME_COLOR: Record<string, string> = {
  pass: 'green', fail: 'red', blocked: 'orange', skipped: 'gray', 'not-run': 'gray',
};
export const SEVERITY_COLOR: Record<string, string> = {
  blocker: 'red', major: 'orange', minor: 'yellow', cosmetic: 'gray',
};

// ─────────────────────────────── Tipi risorsa ───────────────────────────────

interface EmployeeRef { id: string; type?: string; firstName?: string; lastName?: string; email?: string }

export interface TestPlanRow extends Denormalized {
  type: 'test-plans';
  id: string;
  code?: string;
  name?: string;
  description?: string;
  status?: PlanStatus;
  environment?: TestEnvironment;
  appVersion?: string;
  /** CSV di moduli: perimetro della campagna. Vuoto = tutto il catalogo attivo. */
  modules?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
  ownerEmployee?: EmployeeRef | null;
}

export interface TestCaseRow extends Denormalized {
  type: 'test-cases';
  id: string;
  code?: string;
  title?: string;
  /** Dato letto, non scelto: il catalogo può contenere moduli più nuovi di `TEST_MODULES`. */
  module?: string;
  priority?: TestPriority;
  /** Markdown. `steps` è una lista numerata. */
  preconditions?: string;
  steps?: string;
  expectedResult?: string;
  sourceDoc?: string;
  sourceSection?: string;
  tags?: string | null;
  isRegression?: boolean;
  sortOrder?: number;
  isActive?: boolean;
  /** Read-only: `true` = riga del catalogo seedato, `false` = caso scritto dal team. */
  isSeeded?: boolean;
}

export interface TestCaseRunRow extends Denormalized {
  type: 'test-case-runs';
  id: string;
  outcome?: TestOutcome;
  actualResult?: string;
  notes?: string;
  severity?: TestSeverity | null;
  blockedReason?: string | null;
  appVersion?: string;
  environment?: TestEnvironment;
  durationMinutes?: number | null;
  executedAt?: string;
  createdAt?: string;
  testPlan?: { id: string; type?: string } | null;
  testCase?: (TestCaseRow | { id: string; type?: string }) | null;
  executedByEmployee?: EmployeeRef | null;
  retestOfRun?: { id: string; type?: string } | null;
  issueTask?: { id: string; type?: string; taskNumber?: number; title?: string } | null;
}

export interface TestCaseRunAttachmentRow extends Denormalized {
  type: 'test-case-run-attachments';
  id: string;
  attachmentType?: AttachmentType;
  caption?: string;
  uploadedAt?: string;
  testCaseRun?: { id: string; type?: string } | null;
  mediaFile?: { id: string; type?: string } | null;
  uploadedByEmployee?: EmployeeRef | null;
}

// ─────────────────────────────── Permessi ───────────────────────────────

/**
 * I due claim funzionali del modulo. **Non passano da CASL**: `buildAbilityFromJwt` fa
 * `claim.split('.', 2)`, quindi `qa.testcase.write` diventerebbe subject `qa` + azione
 * `testcase`. Si leggono piatti dall'array, come per changelog e fleet.
 */
export const QA_CLAIM_PLAN_WRITE = 'qa.testplan.write';
export const QA_CLAIM_CASE_WRITE = 'qa.testcase.write';

function claimsOf(decoded: DecodedJwt | null | undefined): string[] {
  return Array.isArray(decoded?.claims) ? (decoded!.claims as string[]) : [];
}
export function isQaSuperAdmin(decoded: DecodedJwt | null | undefined): boolean {
  return decoded?.superadmin === 'True';
}
/** Può creare/chiudere campagne. */
export function canWriteTestPlans(decoded: DecodedJwt | null | undefined): boolean {
  return isQaSuperAdmin(decoded) || claimsOf(decoded).includes(QA_CLAIM_PLAN_WRITE);
}
/** Può definire i flussi del catalogo. */
export function canWriteTestCases(decoded: DecodedJwt | null | undefined): boolean {
  return isQaSuperAdmin(decoded) || claimsOf(decoded).includes(QA_CLAIM_CASE_WRITE);
}

// ─────────────────────────────── Letture ───────────────────────────────

/**
 * CSV `modules` → lista di moduli. Vuoto/assente = nessuna restrizione.
 *
 * **Non si filtra su `TEST_MODULES`.** Il perimetro è la stringa che ha scritto il BE, ed è così
 * che la legge `Coverage()` (`QaTestRunController.cs`): scartare i valori "sconosciuti" faceva
 * collassare il perimetro a vuoto, e un perimetro vuoto significa *tutto il catalogo*. Su una
 * campagna con `modules: "tenant-config"` — un modulo nato dopo la nostra lista — la copertura in
 * testa diceva 7 flussi e la checklist sotto ne elencava 304: due numeri diversi per la stessa
 * cosa, e il tester non ha modo di sapere quale dei due sia il perimetro.
 */
export function parseModules(csv?: string | null): string[] {
  if (!csv) return [];
  return csv.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Catalogo dei flussi **attivi**, ordinato come si legge (`sortOrder`, poi `code`).
 * `fetchAllPages`: il catalogo supererà le 100 righe e il clamp le troncherebbe in silenzio.
 */
export function fetchActiveTestCases(modules: string[] = []): Promise<TestCaseRow[]> {
  const filter: Record<string, FilterClause> = { isActive: { eq: true } };
  if (modules.length > 0) filter.module = { in: [...modules] };
  return fetchAllPages<TestCaseRow>('test-cases', { filter, sort: ['sortOrder', 'code'] });
}

/** Tutte le esecuzioni di una campagna, più recenti prima, con il flusso incluso. */
export function fetchPlanRuns(planId: string): Promise<TestCaseRunRow[]> {
  return fetchAllPages<TestCaseRunRow>('test-case-runs', {
    filter: { 'testPlan.id': { eq: planId } },
    include: ['testCase', 'executedByEmployee', 'issueTask'],
    sort: ['-executedAt'],
  });
}

/**
 * **Tutte** le esecuzioni, di ogni campagna, per la vista generale del cruscotto.
 *
 * Il BE aggrega la copertura per campagna (`/coverage`), non sul totale: la domanda «questo
 * flusso l'abbiamo mai provato, in qualunque campagna» non ha un endpoint, e sommare i
 * `/coverage` sarebbe sbagliato — lo stesso flusso sta in piu' campagne e verrebbe contato
 * due volte. Si scaricano quindi le run e si tiene l'ultima per flusso (`coperturaGenerale`).
 *
 * Solo `testCase`: gli altri include (dipendente, task) qui non servono e allargherebbero
 * una risposta che e' gia' la piu' pesante della pagina.
 */
export function fetchAllRuns(): Promise<TestCaseRunRow[]> {
  return fetchAllPages<TestCaseRunRow>('test-case-runs', {
    include: ['testCase'],
    sort: ['-executedAt'],
  });
}

export function fetchRunAttachments(runId: string) {
  return fetchList<TestCaseRunAttachmentRow>('test-case-run-attachments', {
    filter: { 'testCaseRun.id': { eq: runId } },
    include: ['mediaFile'],
    /**
     * `createdAt` e **non** `uploadedAt`: sull'entità BE `UploadedAt` è dichiarato
     * `[Attr(Capabilities = AttrCapabilities.AllowView)]`, cioè sola lettura — ordinarci
     * sopra fa rispondere 400 «Sorting on attribute 'uploadedAt' is not allowed», e la
     * lista degli allegati non arriva mai. L'effetto era subdolo: l'upload andava a buon
     * fine (201 su `fileUpload` e sull'attachment) ma la rilettura falliva, e la schermata
     * diceva "nessuna prova allegata" — l'utente non poteva sapere se il file c'era.
     *
     * `CreatedAt` è un `[Attr]` pieno e per un allegato coincide con l'istante di upload.
     * Da segnalare al BE come incoerenza: sull'entità gemella `TestCaseRun` il campo
     * `ExecutedAt` ha `AllowView | AllowFilter | AllowSort`.
     */
    sort: ['-createdAt'],
    page: { size: 50 },
  });
}

// ─────────────────────────────── Copertura ───────────────────────────────

export interface CoverageModuleRow {
  module: string;
  total: number;
  pass: number;
  fail: number;
  blocked: number;
  skipped: number;
  notRun: number;
}
export interface CoverageNotExecutedRow {
  code: string;
  title: string;
  module: string;
  priority: string;
}
export interface PlanCoverage {
  planCode?: string;
  environment?: string;
  appVersion?: string;
  total: number;
  executed: number;
  executedPercent: number;
  blockers: number;
  byModule: CoverageModuleRow[];
  notExecuted: CoverageNotExecutedRow[];
}

/**
 * `GET /api/test-plans/{id}/coverage`. Conta **l'ultima** esecuzione di ciascun flusso:
 * un caso fallito e poi ripassato è `pass`, non `1 fail + 1 pass`.
 */
export async function fetchPlanCoverage(planId: string): Promise<PlanCoverage | null> {
  const doc = await httpRequest<{ data?: { attributes?: Partial<PlanCoverage> } }>(
    `/api/test-plans/${encodeURIComponent(planId)}/coverage`,
  );
  const a = doc?.data?.attributes;
  if (!a) return null;
  return {
    planCode: a.planCode,
    environment: a.environment,
    appVersion: a.appVersion,
    total: a.total ?? 0,
    executed: a.executed ?? 0,
    executedPercent: a.executedPercent ?? 0,
    blockers: a.blockers ?? 0,
    byModule: Array.isArray(a.byModule) ? a.byModule : [],
    notExecuted: Array.isArray(a.notExecuted) ? a.notExecuted : [],
  };
}

// ─────────────────────────── Checklist (merge lato client) ───────────────────────────

export interface ChecklistItem {
  testCase: TestCaseRow;
  /** Ultima esecuzione nella campagna, `null` se il flusso non è mai stato eseguito. */
  lastRun: TestCaseRunRow | null;
  /** Esito effettivo: `not-run` anche quando non esiste nessuna esecuzione. */
  outcome: TestOutcome;
  /** Quante volte il flusso è stato tentato in questa campagna. */
  attempts: number;
  /**
   * **Tutti** i tentativi, dal più recente. Il BE non li sovrascrive mai — ogni riprova è una riga
   * nuova che punta alla precedente con `retestOfRun` — e il frontend li scaricava già tutti per
   * calcolare `lastRun`: li buttava via subito dopo. Sono lo storico, e serve vederlo: «rotto in
   * v1.78.0, ripassato in v1.78.1» è l'unica prova che una correzione ha funzionato.
   */
  runs: TestCaseRunRow[];
}

/** Id del flusso di una run, sia che `testCase` sia incluso sia che sia un solo riferimento. */
export function runCaseId(run: TestCaseRunRow): string | null {
  const tc = run.testCase;
  if (!tc || typeof tc !== 'object') return null;
  return (tc as { id?: string }).id ?? null;
}

/**
 * Unisce catalogo ed esecuzioni: per ogni flusso l'**ultima** run e il numero di tentativi.
 *
 * Logica pura e testata (`qaChecklist.test.ts`) perché è il cuore della schermata di
 * esecuzione e perché il BE non espone (ancora) un endpoint checklist: senza un ordinamento
 * deterministico due tester che eseguono lo stesso flusso darebbero esiti "a caso".
 * L'ultima è quella con `executedAt` massimo; a parità vince l'id maggiore, così il
 * risultato non dipende dall'ordine di arrivo della lista.
 */
export function buildChecklist(cases: TestCaseRow[], runs: TestCaseRunRow[]): ChecklistItem[] {
  const byCase = new Map<string, TestCaseRunRow[]>();
  for (const run of runs) {
    const cid = runCaseId(run);
    if (!cid) continue;
    const arr = byCase.get(cid);
    if (arr) arr.push(run); else byCase.set(cid, [run]);
  }

  return cases.map((testCase) => {
    const list = byCase.get(testCase.id) ?? [];
    let last: TestCaseRunRow | null = null;
    for (const r of list) {
      if (!last) { last = r; continue; }
      const a = r.executedAt ?? '';
      const b = last.executedAt ?? '';
      if (a > b || (a === b && r.id > last.id)) last = r;
    }
    /* Dal più recente, con lo stesso criterio di `last`: a parità di istante vince l'id maggiore,
       così due tester che salvano nello stesso secondo non producono un ordine diverso a ogni
       ricarica. */
    const runs = [...list].sort((a, b) => {
      const ea = a.executedAt ?? '';
      const eb = b.executedAt ?? '';
      if (ea !== eb) return ea < eb ? 1 : -1;
      return a.id < b.id ? 1 : -1;
    });

    return {
      testCase,
      lastRun: last,
      outcome: (last?.outcome ?? 'not-run') as TestOutcome,
      attempts: list.length,
      runs,
    };
  });
}

/** Conteggi per esito su una checklist (per le KPI locali, senza ricontattare il BE). */
export function checklistTotals(items: ChecklistItem[]): Record<TestOutcome, number> & { blockers: number } {
  const out = { 'not-run': 0, pass: 0, fail: 0, blocked: 0, skipped: 0, blockers: 0 };
  for (const it of items) {
    out[it.outcome] += 1;
    if (it.outcome === 'fail' && it.lastRun?.severity === 'blocker') out.blockers += 1;
  }
  return out;
}

// ─────────────────────────────── Scritture ───────────────────────────────

export interface PlanDraft {
  code: string;
  name: string;
  description?: string;
  status: PlanStatus;
  environment: TestEnvironment;
  appVersion?: string;
  modules: TestModule[];
  startDate?: string | null;
  endDate?: string | null;
  ownerEmployeeId?: string | null;
}

/**
 * `YYYY-MM-DD`: `startDate`/`endDate` sono date pure, non datetime.
 *
 * ⚠️ Era una funzione che ricomponeva la stringa dai componenti locali di un `Date`. Dal
 * 17-09-2026 il picker la stringa la da' gia' fatta, quindi qui resta solo la troncatura di un
 * eventuale istante: la fa `toDateOnly`, che e' la stessa cosa scritta una volta per tutti.
 */
const dateOnly = toDateOnly;

export function createPlan(draft: PlanDraft) {
  return createOne<TestPlanRow>('test-plans', {
    attributes: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      description: draft.description?.trim() || null,
      status: draft.status,
      environment: draft.environment,
      appVersion: draft.appVersion?.trim() || null,
      modules: draft.modules.length ? draft.modules.join(',') : null,
      startDate: dateOnly(draft.startDate),
      endDate: dateOnly(draft.endDate),
    },
    relationships: draft.ownerEmployeeId
      ? { ownerEmployee: { data: { type: 'erp-employees', id: draft.ownerEmployeeId } } }
      : undefined,
  });
}

export function updatePlanStatus(id: string, status: PlanStatus) {
  return updateOne<TestPlanRow>('test-plans', { id, attributes: { status } });
}

export function deletePlan(id: string) {
  return deleteOne('test-plans', id);
}

export interface RunDraft {
  outcome: TestOutcome;
  actualResult?: string;
  notes?: string;
  severity?: TestSeverity | null;
  blockedReason?: string | null;
  durationMinutes?: number | null;
  /** Omessi = ereditati dalla campagna. Valorizzati = vincono. */
  appVersion?: string | null;
  environment?: TestEnvironment | null;
  /** Id dell'esecuzione che questa ritenta. */
  retestOfRunId?: string | null;
}

/**
 * Le due regole condizionali del contratto, validate anche lato client: il BE risponde
 * 422 `TEST_SEVERITY_REQUIRED` / `TEST_BLOCKED_REASON_REQUIRED`, ma far aspettare un
 * round-trip per dire "manca la severità" è tempo perso per il tester.
 */
export function validateRunDraft(d: RunDraft): Record<string, string> {
  const errs: Record<string, string> = {};
  if (d.outcome === 'fail' && !d.severity) {
    errs.severity = 'Obbligatoria quando l\'esito è «Fallito».';
  }
  if (d.outcome === 'blocked' && !d.blockedReason?.trim()) {
    errs.blockedReason = 'Obbligatorio quando l\'esito è «Bloccato»: scrivi cosa impedisce il test.';
  }
  return errs;
}

export function createRun(planId: string, caseId: string, d: RunDraft) {
  const attributes: Record<string, unknown> = {
    outcome: d.outcome,
    actualResult: d.actualResult?.trim() || null,
    notes: d.notes?.trim() || null,
    // Il BE le pretende solo nei rispettivi casi; mandarle sempre sporcherebbe il record.
    severity: d.outcome === 'fail' ? d.severity ?? null : null,
    blockedReason: d.outcome === 'blocked' ? d.blockedReason?.trim() ?? null : null,
    durationMinutes: d.durationMinutes ?? null,
  };
  // Omessi → ereditati dalla campagna (§10): è il comportamento che vogliamo di default.
  if (d.appVersion?.trim()) attributes.appVersion = d.appVersion.trim();
  if (d.environment) attributes.environment = d.environment;

  const relationships: Record<string, { data: { type: string; id: string } }> = {
    testPlan: { data: { type: 'test-plans', id: planId } },
    testCase: { data: { type: 'test-cases', id: caseId } },
  };
  if (d.retestOfRunId) {
    relationships.retestOfRun = { data: { type: 'test-case-runs', id: d.retestOfRunId } };
  }
  return createOne<TestCaseRunRow>('test-case-runs', { attributes, relationships });
}

/** Correzione di un esito già registrato (le stesse regole valgono in update). */
export function updateRun(id: string, d: RunDraft) {
  return updateOne<TestCaseRunRow>('test-case-runs', {
    id,
    attributes: {
      outcome: d.outcome,
      actualResult: d.actualResult?.trim() || null,
      notes: d.notes?.trim() || null,
      severity: d.outcome === 'fail' ? d.severity ?? null : null,
      blockedReason: d.outcome === 'blocked' ? d.blockedReason?.trim() ?? null : null,
      durationMinutes: d.durationMinutes ?? null,
    },
  });
}

export interface CaseDraft {
  code: string;
  title: string;
  module: TestModule;
  priority: TestPriority;
  preconditions?: string;
  steps?: string;
  expectedResult?: string;
  sourceDoc?: string;
  sourceSection?: string;
  tags?: string;
  isRegression: boolean;
  sortOrder?: number | null;
  isActive: boolean;
}

/** `isSeeded` NON si manda: è read-only e scriverlo dà 422. */
function caseAttributes(d: CaseDraft): Record<string, unknown> {
  return {
    code: d.code.trim(),
    title: d.title.trim(),
    module: d.module,
    priority: d.priority,
    preconditions: d.preconditions?.trim() || null,
    steps: d.steps?.trim() || null,
    expectedResult: d.expectedResult?.trim() || null,
    sourceDoc: d.sourceDoc?.trim() || null,
    sourceSection: d.sourceSection?.trim() || null,
    tags: d.tags?.trim() || null,
    isRegression: d.isRegression,
    sortOrder: d.sortOrder ?? null,
    isActive: d.isActive,
  };
}

export function createCase(d: CaseDraft) {
  return createOne<TestCaseRow>('test-cases', { attributes: caseAttributes(d) });
}
export function updateCase(id: string, d: CaseDraft) {
  return updateOne<TestCaseRow>('test-cases', { id, attributes: caseAttributes(d) });
}
export function deleteCase(id: string) {
  return deleteOne('test-cases', id);
}


// ─────────────────────────── Rimando in collaudo (QA-RETEST) ───────────────────────────

/**
 * La richiesta di riprova: chi ha corretto un difetto rimanda il flusso al collaudo.
 *
 * `resolvedByRun` a `null` = **ancora aperta**, cioè nessuno ha riprovato. È il campo su cui si
 * distingue «da fare» da «fatto», e il BE lo scrive da sé quando arriva l'esecuzione di risposta:
 * il client non lo tocca (lo rifiuterebbe).
 */
export interface RetestRequestRow extends Denormalized {
  type: 'test-case-run-retest-requests';
  id: string;
  note?: string | null;
  fixedInAppVersion?: string | null;
  requestedAt?: string;
  testCaseRun?: { id: string; type?: string } | null;
  requestedByEmployee?: EmployeeRef | null;
  resolvedByRun?: { id: string; type?: string } | null;
}

/**
 * Le richieste di riprova di una campagna.
 *
 * Si filtra per campagna passando dall'esecuzione (`testCaseRun.testPlan.id`): non esiste una FK
 * diretta richiesta → campagna, e aggiungerla avrebbe duplicato un'informazione già derivabile —
 * col rischio che le due divergano su uno spostamento di esecuzione.
 */
export function fetchRetestRequestsOfPlan(planId: string) {
  return fetchAllPages<RetestRequestRow>('test-case-run-retest-requests', {
    filter: { 'testCaseRun.testPlan.id': { eq: planId } },
    include: ['requestedByEmployee', 'testCaseRun', 'resolvedByRun'],
    sort: ['-requestedAt'],
  });
}

export interface RequestRetestInput {
  /** Cosa è stato corretto. È la riga che rende utile la riprova. */
  note?: string;
  /** La versione che contiene la correzione. */
  fixedInAppVersion?: string;
}

export interface RequestRetestResult {
  requestId: string | null;
  /** `true` = ce n'era già una aperta: non è partita una seconda mail. */
  alreadyRequested: boolean;
  testCaseCode?: string | null;
  notifiedPlanOwner?: boolean;
  notifiedExecutor?: boolean;
}

/**
 * `POST test-case-runs/{id}/request-retest` — rimanda il flusso in collaudo.
 *
 * Endpoint dedicato e non una PATCH: il gesto crea un fatto con autore e data, e fa partire una
 * notifica **e una mail** a chi ha in carico la campagna. Su una PATCH sarebbe un effetto
 * collaterale invisibile, e chiunque scrivesse sull'esecuzione manderebbe mail senza volerlo.
 *
 * Idempotente: se una richiesta è già aperta torna quella, con `alreadyRequested`.
 */
export async function requestRetest(runId: string, input: RequestRetestInput = {}): Promise<RequestRetestResult> {
  const r = await httpRequest<{
    data?: { id?: string };
    meta?: {
      alreadyRequested?: boolean; testCaseCode?: string;
      notifiedPlanOwner?: boolean; notifiedExecutor?: boolean;
    };
  }>(`/api/test-case-runs/${encodeURIComponent(runId)}/request-retest`, {
    method: 'POST',
    endpointType: 'plain',
    body: {
      note: input.note?.trim() || undefined,
      fixedInAppVersion: input.fixedInAppVersion?.trim() || undefined,
    },
  });
  return {
    requestId: r?.data?.id ?? null,
    alreadyRequested: Boolean(r?.meta?.alreadyRequested),
    testCaseCode: r?.meta?.testCaseCode ?? null,
    notifiedPlanOwner: r?.meta?.notifiedPlanOwner,
    notifiedExecutor: r?.meta?.notifiedExecutor,
  };
}

/** Le richieste aperte, indicizzate per id dell'esecuzione: serve a marcare «da riprovare». */
export function openPerRun(requests: RetestRequestRow[]): Map<string, RetestRequestRow> {
  const out = new Map<string, RetestRequestRow>();
  for (const r of requests) {
    if (r.resolvedByRun) continue;
    const runId = r.testCaseRun?.id;
    if (!runId) continue;
    // Se ce ne fossero due aperte sulla stessa run vince la più recente: la lista arriva
    // ordinata per `-requestedAt`, quindi la prima che si incontra.
    if (!out.has(runId)) out.set(runId, r);
  }
  return out;
}

// ─────────────────────────── report-issue ───────────────────────────

export interface ReportIssueResult {
  taskId: string | null;
  taskNumber?: number;
  title?: string;
  /** `true` = la segnalazione esisteva già, non ne è stata creata una seconda. */
  alreadyLinked: boolean;
  testCaseCode?: string;
}

/**
 * Crea il Task di segnalazione a partire da un'esecuzione. **Idempotente**: se l'esecuzione
 * ha già una segnalazione risponde con quella e `meta.alreadyLinked=true`, senza crearne
 * una seconda — quindi ricliccare non fa danni.
 */
export async function reportIssue(
  runId: string,
  body: { severity?: TestSeverity; assignedId?: string; note?: string } = {},
): Promise<ReportIssueResult> {
  const payload: Record<string, unknown> = {};
  if (body.severity) payload.severity = body.severity;
  if (body.assignedId) payload.assignedId = body.assignedId;
  if (body.note?.trim()) payload.note = body.note.trim();

  const doc = await httpRequest<{
    data?: { id?: string; attributes?: { title?: string; taskNumber?: number } };
    meta?: { alreadyLinked?: boolean; testCaseCode?: string };
  }>(`/api/test-case-runs/${encodeURIComponent(runId)}/report-issue`, {
    method: 'POST',
    body: payload,
    endpointType: 'plain',
  });

  return {
    taskId: doc?.data?.id ?? null,
    taskNumber: doc?.data?.attributes?.taskNumber,
    title: doc?.data?.attributes?.title,
    alreadyLinked: doc?.meta?.alreadyLinked === true,
    testCaseCode: doc?.meta?.testCaseCode,
  };
}

/**
 * Riporta le prove dell'esecuzione sul task di segnalazione.
 *
 * `/report-issue` compone una descrizione ricchissima ma **non porta gli allegati**: chi
 * riceve il bug legge «vedi screenshot» e non ha lo screenshot. Il collegamento lo fa il
 * FE riusando gli stessi `media-files` — nessun file viene ricaricato, si crea solo la
 * riga di raccordo, quindi non c'è duplicazione di byte.
 *
 * Non è un'operazione critica: se una riga fallisce il task esiste già ed è la cosa che
 * conta, quindi si raccolgono gli errori e si restituisce quante ne sono passate invece di
 * far fallire tutta la segnalazione.
 */
export async function linkRunAttachmentsToTask(
  taskId: string,
  attachments: TestCaseRunAttachmentRow[],
): Promise<{ linked: number; failed: number }> {
  let linked = 0, failed = 0;
  for (const a of attachments) {
    const mediaId = a.mediaFile?.id;
    if (!mediaId) { failed += 1; continue; }
    try {
      await createOne('task-attachments', {
        attributes: {
          /* `attachedAt` non si manda: lo scrive il server (§5b della v3) e un valore nostro viene
             ignorato. Qui l'orologio giusto è quello dell'aggancio al task, non quello della prova. */
          description: a.caption?.trim() || `Prova di collaudo (${a.attachmentType ?? 'allegato'})`,
        },
        relationships: {
          task: { data: { type: 'tasks', id: taskId } },
          mediaFile: { data: { type: 'media-files', id: mediaId } },
        },
      });
      linked += 1;
    } catch { failed += 1; }
  }
  return { linked, failed };
}

// ─────────────────────────── Allegati ───────────────────────────

/**
 * Collega un media già caricato a un'esecuzione.
 *
 * ⚠️ Il file va caricato con `uploadMediaFile()` → `POST /api/fileUpload`. Il doc del BE
 * indica `POST /api/UploadFile`, che però è la **risorsa legacy** `UploadFile`: produrrebbe
 * un id non usabile in questa relationship (verificato: GET su quella rotta risponde con
 * una collection JSON:API, non è un endpoint multipart).
 */
export function attachToRun(runId: string, mediaFileId: string, attachmentType: AttachmentType, caption?: string) {
  return createOne<TestCaseRunAttachmentRow>('test-case-run-attachments', {
    attributes: { attachmentType, caption: caption?.trim() || null },
    relationships: {
      testCaseRun: { data: { type: 'test-case-runs', id: runId } },
      mediaFile: { data: { type: 'media-files', id: mediaFileId } },
    },
  });
}

export function deleteAttachment(id: string) {
  return deleteOne('test-case-run-attachments', id);
}

// ─────────────────────────── Seed del catalogo ───────────────────────────

export interface SeedResult {
  created: number;
  updated: number;
  skippedManual: number;
  catalogSize?: number;
}

/**
 * Ripopola il catalogo dai doc di contratto. Richiede **super-admin**; è idempotente su
 * `(tenant, code)` e non tocca i casi scritti a mano (`isSeeded=false`). Va rilanciato a
 * ogni deploy che allarga il catalogo.
 */
export async function seedTestCases(): Promise<SeedResult> {
  const doc = await httpRequest<{
    created?: number; updated?: number; skippedManual?: number;
    data?: { created?: number; updated?: number; skippedManual?: number }[];
    meta?: { catalogSize?: number };
  }>('/api/test-cases/seed', { method: 'POST', body: {}, endpointType: 'plain' });

  // La risposta è per-tenant: si sommano le righe quando arriva un elenco.
  const rows = Array.isArray(doc?.data) ? doc.data : [];
  const sum = (k: 'created' | 'updated' | 'skippedManual') =>
    rows.length > 0 ? rows.reduce((n, r) => n + (r[k] ?? 0), 0) : (doc?.[k] ?? 0);

  return {
    created: sum('created'),
    updated: sum('updated'),
    skippedManual: sum('skippedManual'),
    catalogSize: doc?.meta?.catalogSize,
  };
}
